<script lang="ts">
	/**
	 * One metric over time, with the equal-length window before it drawn behind (§12.10).
	 *
	 * **A number with no comparison is not information**, which is why the previous period is on the
	 * chart rather than in a caption: "1,412 visits" says nothing until you know last month was 900.
	 * It is drawn as a **dashed line in the same hue**, because it is the same entity in a different
	 * window — a second colour would say it was a different thing, and colour here follows the
	 * entity rather than the position on the chart.
	 *
	 * The previous window's points are re-indexed onto **this** window's buckets, so the two lines
	 * are read against one x-axis. Two axes is the mistake this avoids: two time scales side by side
	 * makes any pair of lines cross wherever you like.
	 *
	 * Money is one series **per currency** and never one summed series (§4): minor units in two
	 * currencies are different units, and a single revenue line over a two-currency store is a
	 * plausible-looking number that is not money in any currency. The legend and the table under it
	 * are the relief the palette's light-mode contrast obliges.
	 */

	import type { AnalyticsSeries } from '../../AppTypes';
	import { formatAmount } from '../../stores/storefront';
	import { baseChartOptions, chartTheme, seriesColor } from './chartTheme';
	import ApexChart from './ApexChart.svelte';

	export let series: AnalyticsSeries[] = [];
	export let previousSeries: AnalyticsSeries[] = [];
	/** Names the metric. A single-series chart needs no legend box — the title is the label. */
	export let title: string = '';
	/** True for revenue, which is minor units and formatted per currency. */
	export let isMoney: boolean = false;
	export let granularity: string = 'day';
	export let height: number = 280;

	$: theme = chartTheme();

	function fnBucketLabel(bucket: string): string {
		const date = new Date(bucket);
		if (Number.isNaN(date.getTime())) return bucket;
		if (granularity === 'month') {
			return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
		}
		return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
	}

	/** This window's buckets are the axis; the previous window is laid alongside them by position. */
	$: categories = (series[0]?.points ?? []).map(point => fnBucketLabel(point.bucket));

	$: seriesName = (entry: AnalyticsSeries): string =>
		entry.currency === null ? title : `${title} (${entry.currency.toUpperCase()})`;

	$: apexSeries = [
		...series.map(entry => ({
			name: seriesName(entry),
			data: entry.points.map(point => point.value)
		})),
		...previousSeries.map(entry => ({
			name: `${seriesName(entry)} — previous`,
			data: entry.points.map(point => point.value)
		}))
	];

	/*
	 * The previous window reuses its own series' colour, so the pair reads as one entity in two
	 * windows. Assigned by position in `series`, not by position in the combined list — otherwise a
	 * store adding a second currency would repaint the first one's history.
	 */
	$: colors = [
		...series.map((_entry, index) => seriesColor(index, theme)),
		...previousSeries.map((_entry, index) => seriesColor(index, theme))
	];

	$: dashArray = [...series.map(() => 0), ...previousSeries.map(() => 5)];

	$: options = {
		...baseChartOptions(theme, height),
		chart: { ...(baseChartOptions(theme, height).chart as object), type: 'line' },
		series: apexSeries,
		colors,
		// 2px lines, markers only on hover: a marker on every point of a ninety-day range is a
		// dotted band rather than a line.
		stroke: { width: 2, curve: 'straight', dashArray },
		markers: { size: 0, hover: { size: 5 } },
		xaxis: { ...(baseChartOptions(theme, height).xaxis as object), categories, tickAmount: 8 },
		yaxis: {
			...(baseChartOptions(theme, height).yaxis as object),
			labels: {
				style: { colors: theme.mutedInk, fontSize: '11px' },
				formatter: (value: number) => fnAxis(value)
			}
		},
		// A crosshair plus a shared tooltip: the question a trend chart is read with is "what were
		// all of these on that day", and a per-series tooltip answers a different one.
		tooltip: {
			...(baseChartOptions(theme, height).tooltip as object),
			x: { show: true },
			y: { formatter: (value: number) => fnValue(value) }
		},
		// A legend for two or more series, none for one — the title already names a single line.
		legend: {
			...(baseChartOptions(theme, height).legend as object),
			show: apexSeries.length > 1
		}
	};

	function fnAxis(value: number): string {
		if (!isMoney) return String(Math.round(value));
		const currency = series[0]?.currency ?? '';
		return $formatAmount(Math.round(value), currency) ?? String(Math.round(value));
	}

	function fnValue(value: number): string {
		return fnAxis(value);
	}
</script>

<ApexChart {options} label={`${title} over time`}>
	<svelte:fragment slot="fallback">
		<p class="text-default text-sm py-4">
			This chart could not be drawn. The same figures are in the table below.
		</p>
	</svelte:fragment>
</ApexChart>
